import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from 'react-select';
import imageCompression from 'browser-image-compression';

// Helper to get days in month
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
// Helper to get first day of week (0=Sun, 1=Mon...)
function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}
// Helper to format date as yyyy-mm-dd
function formatDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function CreateOrder() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://teenagefashion.in/backend/api' 
      : 'http://localhost/newfashion/backend/api');
  
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [measurements, setMeasurements] = useState(null);
  const [measurementsLoading, setMeasurementsLoading] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: "",
    delivery_date: "",
    status: "Pending", // Capitalized and with space
    total_amount: "",
    paid: "",
    balance: "",
    quantity: 1,
    fabric_type: "",
    color: "",
    note: ""
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [showMeasurementsEdit, setShowMeasurementsEdit] = useState(false);
  const [measurementsEditData, setMeasurementsEditData] = useState(null);
  const [measurementsEditLoading, setMeasurementsEditLoading] = useState(false);
  const [measurementsEditSuccess, setMeasurementsEditSuccess] = useState("");
  const [measurementsEditError, setMeasurementsEditError] = useState("");
  const [originalMeasurements, setOriginalMeasurements] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  const [orderCounts, setOrderCounts] = useState({});
  const [orderCountsLoading, setOrderCountsLoading] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef(null);

  // Function to fetch order counts for a specific month range (only pending and in-progress)
  const fetchOrderCountsForMonth = useCallback((year, month) => {
    setOrderCountsLoading(true);
    const startDate = new Date(year, month - 1, 1); // Previous month start (local)
    const endDate = new Date(year, month + 1, 0);   // Current month end (local)

    // Build local YYYY-MM-DD strings to avoid UTC shift
    const startStr = formatDate(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );
    const endStr = formatDate(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );
    
    fetch(`${API_URL}/order_day_count.php?start_date=${startStr}&end_date=${endStr}&status_filter=pending,In Progress`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setOrderCounts(data.data || {});
        setOrderCountsLoading(false);
      })
      .catch(() => setOrderCountsLoading(false));
  }, [API_URL]);

  // Close calendar on outside click
  useEffect(() => {
    if (!calendarOpen) return;
    function handleClick(e) {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setCalendarOpen(false);
      }
    }
    function handleEsc(e) {
      if (e.key === 'Escape') setCalendarOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [calendarOpen]);

  useEffect(() => {
    fetch(`${API_URL}/customers.php?all=1`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        setCustomers(Array.isArray(data) ? data : data.data || []);
        setCustomersLoading(false);
      })
      .catch((err) => {
        setCustomers([]);
        setCustomersLoading(false);
        // Optionally handle error
      });

    // Fetch order counts for calendar - get data for current month and surrounding months
    const today = new Date();
    fetchOrderCountsForMonth(today.getFullYear(), today.getMonth());
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customer_id: customerId
    }));
    
    // Fetch measurements for the selected customer
    if (customerId) {
      fetchMeasurements(customerId);
    } else {
      setMeasurements(null);
    }
  };

  const fetchMeasurements = async (customerId) => {
    setMeasurementsLoading(true);
    try {
      const response = await fetch(`${API_URL}/measurements.php?customer_id=${customerId}`, { credentials: 'include' });
      const data = await response.json();
      setMeasurements(data);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      setMeasurements(null);
    } finally {
      setMeasurementsLoading(false);
    }
  };

  const openMeasurementsEdit = () => {
    if (measurements) {
      setMeasurementsEditData({
        id: selectedCustomer.id,
        measurements: measurements
      });
      // Store original measurements for comparison
      setOriginalMeasurements(JSON.parse(JSON.stringify(measurements)));
      setShowMeasurementsEdit(true);
      setMeasurementsEditSuccess("");
      setMeasurementsEditError("");
    }
  };

  const closeMeasurementsEdit = () => {
    setShowMeasurementsEdit(false);
    setMeasurementsEditData(null);
    setOriginalMeasurements(null);
    setMeasurementsEditSuccess("");
    setMeasurementsEditError("");
  };

  const hasMeasurementsChanged = () => {
    if (!originalMeasurements || !measurementsEditData) return false;
    
    const original = originalMeasurements;
    const current = measurementsEditData.measurements;
    
    // Check all measurement fields
    const measurementFields = [
      // Shirt measurements
      'l_shirt', 's_shirt', 'h1_shirt', 'h2_shirt', 'h3_shirt', 'h4_shirt', 'c_shirt', 'w_shirt', 'bx_shirt', 'loos1_shirt', 'loos2_shirt', 'loos3_shirt', 'n_shirt', 'cpoint_shirt', 'pocket_shirt',
      // Pant measurements
      'l_pant', 'w_pant', 'bx_pant', 'f_pant', 'l2_pant', 'b_pant', 'knee_pant', 'thigh_pant', 'pocket_pant', 'fleet_pant',
      // Churidar measurements
      'l_cd', 's_cd', 'h1_cd', 'h2_cd', 'h3_cd', 'h4_cd', 'c_cd', 'w_cd', 'h_cd', 'bx_cd', 'n_f_cd', 'n_b_cd'
    ];
    
    for (const field of measurementFields) {
      if (original[field] !== current[field]) {
        return true;
      }
    }
    
    return false;
  };

  const handleMeasurementsUpdate = async (e) => {
    e.preventDefault();
    
    // Check if any changes were made
    if (!hasMeasurementsChanged()) {
      setMeasurementsEditSuccess("No changes detected. Closing modal...");
      setTimeout(() => {
        closeMeasurementsEdit();
      }, 1000);
      return;
    }

    setMeasurementsEditLoading(true);
    setMeasurementsEditError("");
    setMeasurementsEditSuccess("");

    // Prepare payload
    const payload = {
      customer_id: measurementsEditData.id,
      // Shirt measurements
      l_shirt: measurementsEditData.measurements.l_shirt || 0,
      s_shirt: measurementsEditData.measurements.s_shirt || 0,
      h1_shirt: measurementsEditData.measurements.h1_shirt || 0,
      h2_shirt: measurementsEditData.measurements.h2_shirt || 0,
      h3_shirt: measurementsEditData.measurements.h3_shirt || 0,
      h4_shirt: measurementsEditData.measurements.h4_shirt || 0,
      c_shirt: measurementsEditData.measurements.c_shirt || 0,
      w_shirt: measurementsEditData.measurements.w_shirt || 0,
      bx_shirt: measurementsEditData.measurements.bx_shirt || 0,
      loos1_shirt: measurementsEditData.measurements.loos1_shirt || 0,
      loos2_shirt: measurementsEditData.measurements.loos2_shirt || 0,
      loos3_shirt: measurementsEditData.measurements.loos3_shirt || 0,
      n_shirt: measurementsEditData.measurements.n_shirt || 0,
      cpoint_shirt: measurementsEditData.measurements.cpoint_shirt || 0,
      pocket_shirt: measurementsEditData.measurements.pocket_shirt || 0,
      // Pant measurements
      l_pant: measurementsEditData.measurements.l_pant || 0,
      w_pant: measurementsEditData.measurements.w_pant || 0,
      bx_pant: measurementsEditData.measurements.bx_pant || 0,
      f_pant: measurementsEditData.measurements.f_pant || 0,
      l2_pant: measurementsEditData.measurements.l2_pant || 0,
      b_pant: measurementsEditData.measurements.b_pant || 0,
      knee_pant: measurementsEditData.measurements.knee_pant || 0,
      thigh_pant: measurementsEditData.measurements.thigh_pant || 0,
      pocket_pant: measurementsEditData.measurements.pocket_pant || 0,
      fleet_pant: measurementsEditData.measurements.fleet_pant || 0,
      // Churidar measurements
      l_cd: measurementsEditData.measurements.l_cd || 0,
      s_cd: measurementsEditData.measurements.s_cd || 0,
      h1_cd: measurementsEditData.measurements.h1_cd || 0,
      h2_cd: measurementsEditData.measurements.h2_cd || 0,
      h3_cd: measurementsEditData.measurements.h3_cd || 0,
      h4_cd: measurementsEditData.measurements.h4_cd || 0,
      c_cd: measurementsEditData.measurements.c_cd || 0,
      w_cd: measurementsEditData.measurements.w_cd || 0,
      h_cd: measurementsEditData.measurements.h_cd || 0,
      bx_cd: measurementsEditData.measurements.bx_cd || 0,
      n_f_cd: measurementsEditData.measurements.n_f_cd || 0,
      n_b_cd: measurementsEditData.measurements.n_b_cd || 0,
    };

    try {
      const res = await fetch(`${API_URL}/update_measurements.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      const result = await res.json();
      if (result.success) {
        setMeasurementsEditSuccess("Measurements updated successfully!");
        // Update local measurements state
        setMeasurements(measurementsEditData.measurements);
        setTimeout(() => {
          closeMeasurementsEdit();
        }, 1000);
      } else {
        setMeasurementsEditError(result.error || "Failed to update measurements.");
      }
    } catch (err) {
      setMeasurementsEditError("Failed to update measurements.");
    } finally {
      setMeasurementsEditLoading(false);
    }
  };

  // Calculate balance when total_amount or paid changes
  const calculateBalance = () => {
    const total = parseFloat(formData.total_amount) || 0;
    const paid = parseFloat(formData.paid) || 0;
    return total - paid;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer_id) {
      newErrors.customer_id = "Please select a customer";
    }
    
    if (!formData.delivery_date) {
      newErrors.delivery_date = "Delivery date is required";
    }
    
    // fabric_type and color are now optional
    if (formData.quantity < 1) {
      newErrors.quantity = "Quantity must be at least 1";
    }

    if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
      newErrors.total_amount = "Total amount must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const balance = calculateBalance();
      const orderData = {
        customer_id: formData.customer_id,
        delivery_date: formData.delivery_date,
        status: formData.status,
        created_at: new Date().toISOString(),
        total_amount: formData.total_amount,
        paid: formData.paid,
        balance: balance.toFixed(2),
        quantity: formData.quantity,
        fabric_type: formData.fabric_type,
        color: formData.color,
        note: formData.note
      };

      // Prepare FormData for multipart/form-data
      const formDataToSend = new FormData();
      Object.entries(orderData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      // If an image is selected, compress and append
      if (selectedImage) {
        try {
          const compressedFile = await imageCompression(selectedImage, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1024,
            useWebWorker: true
          });
          formDataToSend.append('image', compressedFile, compressedFile.name);
        } catch (err) {
          setAlert({ type: "error", message: "Image compression failed. Please try again." });
          setIsSubmitting(false);
          return;
        }
      }

      const response = await fetch(`${API_URL}/create_order_with_image.php`, {
        method: "POST",
        body: formDataToSend,
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        navigate("/view-orders", { state: { alert: { type: "success", message: "Order saved successfully!" } } });
      } else {
        setAlert({ type: "error", message: result.error || "Failed to create order. Please try again." });
      }
    } catch (error) {
      setAlert({ type: "error", message: "Failed to create order. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
  };

  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Create New Order</h1>
          <p className="text-gray-600">Select customer and enter order details</p>
        </div>

        {alert.message && (
          <div className={`mb-4 p-3 rounded ${alert.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
            {alert.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Selection & Order Basics */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="customer_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Select Customer *
                </label>
                <Select
                  options={customers.map(c => ({
                    value: c.id,
                    label: `${c.name} - ${c.phone} (${c.address})`
                  }))}
                  onChange={option => handleCustomerSelect(option.value)}
                  isLoading={customersLoading}
                  placeholder="Search or select a customer..."
                />
                {customersLoading && <p className="text-sm text-gray-500">Loading customers...</p>}
                {errors.customer_id && <p className="text-red-500 text-sm mt-1">{errors.customer_id}</p>}
              </div>

              <div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delivery Date *
                  </label>
                  <button
                    type="button"
                    className={`w-full px-3 py-2 border rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between ${errors.delivery_date ? 'border-red-300' : 'border-gray-300'} bg-white`}
                    onClick={() => setCalendarOpen(true)}
                  >
                    <span>
                      {formData.delivery_date ? new Date(formData.delivery_date).toLocaleDateString() : <span className="text-gray-400">Pick a date</span>}
                    </span>
                    <svg className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {calendarOpen && (
                    <div ref={calendarRef} className="absolute z-20 bg-white border border-gray-200 rounded-lg shadow-lg mt-2 p-4 w-80">
                      <div className="mb-2 flex items-center gap-2 justify-between">
                        <button type="button" onClick={() => {
                          const newMonth = calendarMonth.month === 0 ? 11 : calendarMonth.month - 1;
                          const newYear = calendarMonth.month === 0 ? calendarMonth.year - 1 : calendarMonth.year;
                          setCalendarMonth({ year: newYear, month: newMonth });
                          fetchOrderCountsForMonth(newYear, newMonth);
                        }} className="px-2 py-1 text-gray-500 hover:text-gray-700">&lt;</button>
                        <span className="font-semibold">{new Date(calendarMonth.year, calendarMonth.month).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                        <button type="button" onClick={() => {
                          const newMonth = calendarMonth.month === 11 ? 0 : calendarMonth.month + 1;
                          const newYear = calendarMonth.month === 11 ? calendarMonth.year + 1 : calendarMonth.year;
                          setCalendarMonth({ year: newYear, month: newMonth });
                          fetchOrderCountsForMonth(newYear, newMonth);
                        }} className="px-2 py-1 text-gray-500 hover:text-gray-700">&gt;</button>
                      </div>
                      <div className="grid grid-cols-7 gap-1 text-xs text-center">
                        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
                          <div key={d} className="font-medium text-gray-500">{d}</div>
                        ))}
                        {/* Calendar days */}
                        {(() => {
                          const days = [];
                          const firstDay = getFirstDayOfWeek(calendarMonth.year, calendarMonth.month);
                          const numDays = getDaysInMonth(calendarMonth.year, calendarMonth.month);
                          const today = new Date();
                          today.setHours(0,0,0,0); // Ignore time for comparison
                          for (let i = 0; i < firstDay; i++) days.push(<div key={"empty-"+i}></div>);
                          for (let day = 1; day <= numDays; day++) {
                            const dateStr = formatDate(calendarMonth.year, calendarMonth.month, day);
                            const count = orderCounts[dateStr] || 0;
                            const isSelected = formData.delivery_date === dateStr;
                            const thisDate = new Date(calendarMonth.year, calendarMonth.month, day);
                            thisDate.setHours(0,0,0,0);
                            const isPast = thisDate < today;
                            days.push(
                              <button
                                key={dateStr}
                                type="button"
                                onClick={() => {
                                  if (isPast) return;
                                  handleChange({ target: { name: 'delivery_date', value: dateStr } });
                                  setCalendarOpen(false);
                                }}
                                disabled={isPast}
                                className={`relative w-9 h-9 rounded-lg flex items-center justify-center border transition ${
                                  isSelected ? 'bg-blue-600 text-white border-blue-600' :
                                  isPast ? 'bg-gray-100 text-gray-400 border-gray-100 cursor-not-allowed' :
                                  'bg-white text-gray-900 border-gray-200 hover:bg-blue-50'
                                }`}
                              >
                                {day}
                                {count > 0 && !isPast && (
                                  <span className={`absolute bottom-0 right-0 text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none shadow-lg ${count > 5 ? 'bg-red-500' : 'bg-blue-500'}`}>
                                  {count}
                                </span>
                                )}
                              </button>
                            );
                          }
                          return days;
                        })()}
                      </div>
                      {orderCountsLoading && <p className="text-xs text-gray-400 mt-1">Loading order counts...</p>}
                      {errors.delivery_date && <p className="text-red-500 text-sm mt-1">{errors.delivery_date}</p>}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.quantity ? "border-red-300" : "border-gray-300"
                  }`}
                />
                {errors.quantity && <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Delivered">Delivered</option>
                </select>
              </div>
            </div>
          </div>

          {/* Customer Measurements */}
          {selectedCustomer && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Customer Measurements</h3>
                {measurements && (measurements.l_shirt || measurements.s_shirt || measurements.c_shirt || measurements.l_pant || measurements.w_pant || measurements.l_cd || measurements.s_cd) && (
                  <button
                    type="button"
                    onClick={openMeasurementsEdit}
                    className="text-sm bg-blue-100 text-blue-700 py-1 px-3 rounded-lg hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                  >
                    Quick Edit
                  </button>
                )}
              </div>
              {measurementsLoading ? (
                <div className="text-center py-4">
                  <p className="text-gray-500">Loading measurements...</p>
                </div>
              ) : measurements && (measurements.l_shirt || measurements.s_shirt || measurements.c_shirt || measurements.l_pant || measurements.w_pant || measurements.l_cd || measurements.s_cd) ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Shirt Measurements */}
                    {(() => {
                      const shirtMeasurements = [
                        { key: 'L', value: measurements.l_shirt },
                        { key: 'S', value: measurements.s_shirt },
                        { key: 'H1', value: measurements.h1_shirt },
                        { key: 'H2', value: measurements.h2_shirt },
                        { key: 'H3', value: measurements.h3_shirt },
                        { key: 'H4', value: measurements.h4_shirt },
                        { key: 'C', value: measurements.c_shirt },
                        { key: 'W', value: measurements.w_shirt },
                        { key: 'Bx', value: measurements.bx_shirt },
                        { key: 'Loos1', value: measurements.loos1_shirt },
                        { key: 'Loos2', value: measurements.loos2_shirt },
                        { key: 'Loos3', value: measurements.loos3_shirt },
                        { key: 'N', value: measurements.n_shirt },
                        { key: 'CPoint', value: measurements.cpoint_shirt },
                        { key: 'Pocket', value: measurements.pocket_shirt }
                      ].filter(item => item.value && item.value !== '0' && item.value !== 0);
                      
                      return shirtMeasurements.length > 0 ? (
                        <div>
                          <h4 className="font-medium text-gray-800 mb-3 text-sm">Shirt Measurements</h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {shirtMeasurements.map((item) => (
                              <div key={item.key} className="flex justify-between">
                                <span className="text-gray-600">{item.key}:</span>
                                <span className="font-medium">{item.value} cm</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                    
                    {/* Pant Measurements */}
                    {(() => {
                      const pantMeasurements = [
                        { key: 'L', value: measurements.l_pant },
                        { key: 'W2', value: measurements.w_pant },
                        { key: 'Bx', value: measurements.bx_pant },
                        { key: 'F', value: measurements.f_pant },
                        { key: 'L2', value: measurements.l2_pant },
                        { key: 'B', value: measurements.b_pant },
                        { key: 'Knee', value: measurements.knee_pant },
                        { key: 'Thigh', value: measurements.thigh_pant },
                        { key: 'Pocket', value: measurements.pocket_pant },
                        { key: 'Fleet', value: measurements.fleet_pant }
                      ].filter(item => item.value && item.value !== '0' && item.value !== 0);
                      
                      return pantMeasurements.length > 0 ? (
                        <div>
                          <h4 className="font-medium text-gray-800 mb-3 text-sm">Pant Measurements</h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {pantMeasurements.map((item) => (
                              <div key={item.key} className="flex justify-between">
                                <span className="text-gray-600">{item.key}:</span>
                                <span className="font-medium">{item.value} cm</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                    
                    {/* Ladies Measurements */}
                    {(() => {
                      const ladiesMeasurements = [
                        { key: 'L', value: measurements.l_cd },
                        { key: 'S', value: measurements.s_cd },
                        { key: 'H1', value: measurements.h1_cd },
                        { key: 'H2', value: measurements.h2_cd },
                        { key: 'H3', value: measurements.h3_cd },
                        { key: 'H4', value: measurements.h4_cd },
                        { key: 'C', value: measurements.c_cd },
                        { key: 'W', value: measurements.w_cd },
                        { key: 'H', value: measurements.h_cd },
                        { key: 'Bx', value: measurements.bx_cd },
                        { key: 'NF', value: measurements.n_f_cd },
                        { key: 'NB', value: measurements.n_b_cd }
                      ].filter(item => item.value && item.value !== '0' && item.value !== 0);
                      
                      return ladiesMeasurements.length > 0 ? (
                        <div>
                          <h4 className="font-medium text-gray-800 mb-3 text-sm">Ladies Measurements</h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {ladiesMeasurements.map((item) => (
                              <div key={item.key} className="flex justify-between">
                                <span className="text-gray-600">{item.key}:</span>
                                <span className="font-medium">{item.value} cm</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-700 text-sm">No measurements found for this customer.</p>
                </div>
              )}
            </div>
          )}

          {/* Order Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="fabric_type" className="block text-sm font-medium text-gray-700 mb-1">
                  Fabric Type
                </label>
                <input
                  type="text"
                  id="fabric_type"
                  name="fabric_type"
                  value={formData.fabric_type}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.fabric_type ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="e.g., Cotton, Silk, Linen"
                />
                {errors.fabric_type && <p className="text-red-500 text-sm mt-1">{errors.fabric_type}</p>}
              </div>

              <div>
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="text"
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.color ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="e.g., Navy Blue, Black"
                />
                {errors.color && <p className="text-red-500 text-sm mt-1">{errors.color}</p>}
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="total_amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Total Amount (₹) *
                </label>
                <input
                  type="number"
                  id="total_amount"
                  name="total_amount"
                  value={formData.total_amount}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.total_amount ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="0.00"
                />
                {errors.total_amount && <p className="text-red-500 text-sm mt-1">{errors.total_amount}</p>}
              </div>

              <div>
                <label htmlFor="paid" className="block text-sm font-medium text-gray-700 mb-1">
                  Paid Amount (₹)
                </label>
                <input
                  type="number"
                  id="paid"
                  name="paid"
                  value={formData.paid}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Balance (₹)
                </label>
                <input
                  type="text"
                  value={calculateBalance().toFixed(2)}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              id="note"
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any additional notes about the order..."
            />
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Image Capture Section */}
          <div className="mb-4">
            <label className="block text-gray-700 font-bold mb-2">Order Image (optional)</label>
            {!previewUrl ? (
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            ) : (
              <div className="flex flex-col items-start gap-2">
                <img src={previewUrl} alt="Preview" className="w-32 h-32 object-cover rounded border" />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Remove / Retry
                </button>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isSubmitting ? "Creating Order..." : "Create Order"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 sm:flex-none bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Measurements Edit Modal */}
      {showMeasurementsEdit && measurementsEditData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto p-6 relative">
            <button
              onClick={closeMeasurementsEdit}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-lg font-bold mb-4 text-gray-900">
              Edit Measurements - {selectedCustomer?.name}
            </h2>
            
            <form onSubmit={handleMeasurementsUpdate} className="space-y-6">
              {/* Shirt Measurements */}
              <div>
                <h3 className="font-medium text-gray-800 mb-3 text-sm">Shirt Measurements</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1">L</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.l_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, l_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">S</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.s_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, s_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H1</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h1_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h1_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H2</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h2_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h2_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H3</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h3_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h3_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H4</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h4_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h4_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">C</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.c_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, c_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">W</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.w_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, w_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Bx</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.bx_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, bx_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Loos1</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.loos1_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, loos1_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Loos2</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.loos2_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, loos2_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Loos3</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.loos3_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, loos3_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">N</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.n_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, n_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">CPoint</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.cpoint_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, cpoint_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Pocket</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.pocket_shirt || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, pocket_shirt: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                </div>
              </div>

              {/* Pant Measurements */}
              <div>
                <h3 className="font-medium text-gray-800 mb-3 text-sm">Pant Measurements</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1">L</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.l_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, l_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">W2</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.w_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, w_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Bx</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.bx_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, bx_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">F</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.f_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, f_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">L2</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.l2_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, l2_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">B</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.b_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, b_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Knee</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.knee_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, knee_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Thigh</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.thigh_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, thigh_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Pocket</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.pocket_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, pocket_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Fleet</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.fleet_pant || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, fleet_pant: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                </div>
              </div>

              {/* Ladies Measurements */}
              <div>
                <h3 className="font-medium text-gray-800 mb-3 text-sm">Ladies Measurements</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1">L</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.l_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, l_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">S</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.s_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, s_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H1</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h1_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h1_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H2</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h2_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h2_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H3</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h3_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h3_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H4</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h4_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h4_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">C</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.c_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, c_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">W</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.w_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, w_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">H</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.h_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, h_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Bx</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.bx_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, bx_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">NF</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.n_f_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, n_f_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">NB</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsEditData.measurements.n_b_cd || ''}
                      onChange={e => setMeasurementsEditData(prev => ({
                        ...prev,
                        measurements: { ...prev.measurements, n_b_cd: e.target.value }
                      }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs"
                      placeholder="0.0"
                    />
                  </div>
                </div>
              </div>

              {/* Error and Success Messages */}
              {measurementsEditError && (
                <div className="text-red-500 text-xs">{measurementsEditError}</div>
              )}
              {measurementsEditSuccess && (
                <div className="text-green-600 text-xs">{measurementsEditSuccess}</div>
              )}

              {/* Modal Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={measurementsEditLoading}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
                >
                  {measurementsEditLoading ? "Updating..." : "Update Measurements"}
                </button>
                <button
                  type="button"
                  onClick={closeMeasurementsEdit}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 